// Intent: a notification that slides DOWN from the top edge, rests for a few
// seconds, then slides further down while fading out (owner: "niech zjedzie
// z góry, zostanie na chwilę, a znikając zjeżdża w dół"). Used when tapping a
// ring marker on Start — replaces the old closable bubble at the bottom of the
// card. Non-dismissable by design: it just passes through.

import { useEffect } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withDelay, withTiming } from 'react-native-reanimated';

import { AppText } from '@/components/ui/app-text';
import { colors, radius, shadows, spacing } from '@/theme/tokens';

export interface TopToastContent {
  /** Unique per show so the same marker tapped twice re-triggers the animation. */
  key: number;
  icon: keyof typeof Ionicons.glyphMap;
  iconColor: string;
  iconBg: string;
  title: string;
  subtitle?: string;
}

const REST_MS = 3200;

export function TopToast({ content, onHide }: { content: TopToastContent | null; onHide: () => void }) {
  const insets = useSafeAreaInsets();
  const progress = useSharedValue(0); // 0 = hidden above, 1 = resting near top
  const exit = useSharedValue(0); // 0 = resting, 1 = slid down + faded

  useEffect(() => {
    if (!content) return;
    progress.value = 0;
    exit.value = 0;
    progress.value = withTiming(1, { duration: 320 });
    // After resting, slide down (exit=1); when that finishes, clear the content.
    exit.value = withDelay(
      REST_MS,
      withTiming(1, { duration: 340 }, (finished) => {
        if (finished) runOnJS(onHide)();
      }),
    );
    // re-run whenever a new toast is shown
  }, [content?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const style = useAnimatedStyle(() => ({
    opacity: progress.value * (1 - exit.value),
    transform: [{ translateY: -80 + progress.value * 80 + exit.value * 56 }],
  }));

  if (!content) return null;

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        {
          position: 'absolute',
          top: insets.top + spacing.xs,
          left: spacing.screen,
          right: spacing.screen,
          zIndex: 20,
          flexDirection: 'row',
          alignItems: 'center',
          gap: spacing.sm,
          backgroundColor: colors.card,
          borderRadius: radius.card,
          padding: spacing.md,
          ...shadows.card,
        },
        style,
      ]}
    >
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: radius.full,
          backgroundColor: content.iconBg,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Ionicons name={content.icon} size={22} color={content.iconColor} />
      </View>
      <View style={{ flex: 1 }}>
        <AppText variant="labelLg">{content.title}</AppText>
        {content.subtitle ? (
          <AppText variant="bodyMd" color={colors.textSecondary}>
            {content.subtitle}
          </AppText>
        ) : null}
      </View>
    </Animated.View>
  );
}
